import React, { useState } from "react";
import styled from "styled-components";
import AccountingConfigForm from "components/widgets/forms/setup/Accounting_Config";
import BasicDetails from "components/setup/BasicDetails";
import { fetch_product_details } from "services/apiSrv";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import Loader from "components/widgets/loader";

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 100%;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 16px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-height: 60vh;
  overflow-y: auto;
  margin-bottom: 32px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 8px rgb(0 0 0 / 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    font-size: 14px;
  }

  th {
    background: #f4f4f4;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 8px;
      font-size: 12px;
    }
  }

  @media (max-width: 480px) {
    th,
    td {
      padding: 6px;
      font-size: 10px;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

export default function AccountingConfigPage() {
  const [productItems] = useState([
    {
      id: 1,
      name: "Comprehensive Auto",
      ref: "AUTO123",
      underwriters: "ABC Insurance",
      productImage: "auto.jpg",
    },
  ]);

  const { id: productSystemRef } = useParams();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["productDetails", productSystemRef],
    queryFn: () => fetch_product_details(productSystemRef),
    enabled: !!productSystemRef,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <p>Error fetching product details: {error?.message || "Something went wrong"}</p>;
  }

  if (!data?.data) {
    return <p>No product data available.</p>;
  }



  // Function to map the source accounting config object to the form's initialValues format
  function mapAccountingConfigToFormValues(config) {
    const defaultInitialValues = {
      productAccountingConfigName: "",
      productAccountingSystemRef: "",
      handleTaxes: "",
      generateEtims: "",
      remitTax: "",
      etimsTaxNumber: "",
      etimsToken: "",
      settlementChannel: "",
      mpesaNumber: "",
      mpesaPaybill: "",
      mpesaAccountNumber: "",
      bankName: "",
      accountNumber: "",
      branch: "",
      settlementType: "",
      threshold: "",
      offerCommission: "",
      commissionRate: "",
      paymentMethod: "manual",
    };
  
    return {
      ...defaultInitialValues,
      id: config.id || undefined, // Include id for edit mode
      productAccountingSystemRef:  config.productAccountingSystemRef,
      productAccountingConfigName: config.productAccountingConfigName || "",
      handleTaxes: config.taxType === "auto" ? "yes" : "no",
      generateEtims: config.autoGenerateEtimsCert ? "yes" : "no",
      remitTax: config.autoRemmitTax ? "yes" : "no",
      etimsTaxNumber: config.etimsConfig?.etimsTaxNumber || "",
      etimsToken: config.etimsConfig?.etimsToken || "",
      settlementChannel: config.settlementChannel || "",
      mpesaNumber: config.settlementMpesaNumber || "",
      mpesaPaybill: config.settlementMpesaPaybill || "",
      mpesaAccountNumber: config.settlementMpesaAccountNumber || "", // Assuming account number for paybill
      bankName: config.settlementBankDetails?.bankName || "",
      accountNumber: config.settlementBankDetails?.accountNumber || "",
      branch: config.settlementBankDetails?.branch || "", // Assuming branch might be included
      settlementType: config.settlementType || "",
      threshold: config.settlementTypeThresholdValue ? String(config.settlementTypeThresholdValue) : "",
      offerCommission: config.salesRepCommisionRep !== null ? "yes" : "no",
      commissionRate: config.salesRepCommisionRep ? String(config.salesRepCommisionRep) : "",
      paymentMethod: config.salesRepCommisionSettlementType || "manual",
    };
  }

  const { productPolicyConfig, product_accounting_details } = data.data[0];

  let accountingConfigs = {}

  if(!!product_accounting_details && product_accounting_details){
   accountingConfigs = mapAccountingConfigToFormValues(product_accounting_details)
  }

  

  return (
    <Container>
      <Section>
        <BasicDetails />
      </Section>

      <Section>
        <Title>Accounting Configuration</Title>

        {!isLoading && (<AccountingConfigForm initialConfig={accountingConfigs} refetchProductDetails={refetch} />)}
        
        {product_accounting_details && (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Auto Generate eTIMS Certificate</td>
                  <td>{product_accounting_details.autoGenerateEtimsCert ? "Yes" : "No"}</td>
                </tr>
                <tr>
                  <td>Auto Remit Tax</td>
                  <td>{product_accounting_details.autoRemmitTax ? "Yes" : "No"}</td>
                </tr>
                <tr>
                  <td>Entity Reference</td>
                  <td>{product_accounting_details.entityRef}</td>
                </tr>
                <tr>
                  <td>eTIMS Token</td>
                  <td>{product_accounting_details.etimsConfig?.etimsToken || "N/A"}</td>
                </tr>
                <tr>
                  <td>Product Accounting Config Name</td>
                  <td>{product_accounting_details.productAccountingConfigName}</td>
                </tr>
                <tr>
                  <td>Product Accounting System Reference</td>
                  <td>{product_accounting_details.productAccountingSystemRef}</td>
                </tr>
                <tr>
                  <td>Sales Representative Commission</td>
                  <td>{product_accounting_details.salesRepCommisionRep}</td>
                </tr>
                <tr>
                  <td>Sales Representative Commission Settlement Type</td>
                  <td>{product_accounting_details.salesRepCommisionSettlementType}</td>
                </tr>
                <tr>
                  <td>Settlement Bank Details</td>
                  <td>
                    {product_accounting_details.settlementBankDetails
                      ? `Bank: ${product_accounting_details.settlementBankDetails.bankName}, Account: ${product_accounting_details.settlementBankDetails.accountNumber}`
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <td>Settlement Channel</td>
                  <td>{product_accounting_details.settlementChannel}</td>
                </tr>
                <tr>
                  <td>Settlement M-Pesa Number</td>
                  <td>{product_accounting_details.settlementMpesaNumber || "N/A"}</td>
                </tr>
                <tr>
                  <td>Settlement M-Pesa Paybill</td>
                  <td>{product_accounting_details.settlementMpesaPaybill || "N/A"}</td>
                </tr>
                <tr>
                  <td>Settlement Type</td>
                  <td>{product_accounting_details.settlementType}</td>
                </tr>
                <tr>
                  <td>Settlement Type Threshold Value</td>
                  <td>{product_accounting_details.settlementTypeThresholdValue.toLocaleString()}</td>
                </tr>
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Section>
    </Container>
  );
}